import { useCallback, useContext } from 'react'

import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { EditorPanelItem } from '@editor/interfaces/editor'
import { setIsBatchSaving } from '@editor/pages/projects/editor/store/editor'
import {
  setEditedGroupKeys,
  setInspectionGroups,
  updateInspectionGroups,
} from '@editor/pages/projects/editor/store/inspectionGroup'
import { getInspectionGroups, patchInspectionGroups } from '@editor/services/InspectionGroup'
import { withPromiseContained } from '@editor/services/Util'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { useDebouncedCallback } from 'use-debounce'

/**
 * Custom hook for fetching inspection groups.
 *
 * @param allGroups Whether to fetch all inspection groups or only those related to the current inspection area.
 */
const useInspectionGroup = (allGroups = false) => {
  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const inspectionGroups = useSelector((state: RootState) => state.inspectionGroup.groups)

  /**
   * Fetch inspection groups from the server.
   */
  const fetchInspectionGroups = useCallback(async () => {
    if (!project || (!allGroups && !inspectionArea)) return false

    const result = await withPromiseContained(async () => {
      const token = await getAccessToken()
      if (!token) throw new Error('No access token found')

      // Fetch inspection groups
      const groups = await getInspectionGroups(
        token,
        project.project_id,
        !allGroups ? inspectionArea?.inspection_area_id : undefined,
        showErrorModal,
      )
      if (!groups) throw new Error('Failed to fetch inspection groups')

      // Dispatch to store
      dispatch(
        setInspectionGroups(
          groups.map<EditorPanelItem>((group) => ({
            key: `panel-group-${group.group_id}`,
            item: group,
            name: group.name,
            color: group.color,
            order: group.order,
            isOrderable: true,
            isCollapsible: true,
          })),
        ),
      )

      return true
    }).catch(() => false)

    return result
  }, [project, inspectionArea, allGroups, getAccessToken, showErrorModal, dispatch])

  /**
   * Core logic for updating inspection groups (non-debounced).
   * Used by the unified batch save in Editor.
   */
  const patchGroups = useCallback(
    async (editedKeys: string[]) => {
      if (!project || !inspectionArea || !editedKeys.length) return

      // Mark groups as saving
      dispatch(updateInspectionGroups(editedKeys.map((key) => ({ key, isSaving: true }))))

      // Save to server
      await withPromiseContained(async () => {
        const token = await getAccessToken()
        if (!token) throw new Error('Failed to get access token')

        const groups = inspectionGroups.filter((group) => editedKeys.includes(group.key))
        const result = await patchInspectionGroups(
          token,
          project.project_id,
          groups.map(({ key, name, order, color }) => ({
            group_id: key.replace('panel-group-', ''),
            inspection_area_id: inspectionArea.inspection_area_id,
            name,
            order,
            color,
          })),
        )
        if (!result) throw new Error('Failed to update inspection group')

        // Clear saving state on success
        dispatch(updateInspectionGroups(editedKeys.map((key) => ({ key, isSaving: false }))))
      }).catch(async () => {
        // Clear saving state on error
        dispatch(updateInspectionGroups(editedKeys.map((key) => ({ key, isSaving: false }))))
        // refetch inspection groups to reset state
        await fetchInspectionGroups()
        showErrorModal('検査グループの保存中にエラーが発生しました。\n保存前の状態にデータを戻しました。')
      })
    },
    [project, inspectionArea, inspectionGroups, getAccessToken, fetchInspectionGroups, showErrorModal, dispatch],
  )

  /**
   * Handle batch saving of inspection groups (debounced version).
   */
  const patchGroupsDebounced = useDebouncedCallback(
    async (editedKeys: string[]) => {
      dispatch(setIsBatchSaving(true))
      await patchGroups(editedKeys)
      dispatch(setEditedGroupKeys([])) // Clear edited keys after saving
      dispatch(setIsBatchSaving(false))
    },
    3000,
    {
      leading: false,
      trailing: true,
    },
  )

  return {
    fetchInspectionGroups,
    patchGroups,
    patchGroupsDebounced,
  }
}

export default useInspectionGroup
